'use client';

import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { usernameRegistrationStepAtom } from '../data-access';

import { type RegisterSchemaType } from './useAuthFormHelper';

type StepValidationProps = {
	onValidityChange: (isValid: boolean) => void;
};

type EmailStepValidationProps = StepValidationProps & {
	isTermsAndConditionsVisible: boolean;
};

type UsernameStepValidationProps = StepValidationProps & {
	isCountryStepVisible: boolean;
	isEmailStepVisible: boolean;
};

export const useEmailAddressStepValidation = ({
	isTermsAndConditionsVisible,
	onValidityChange
}: EmailStepValidationProps) => {
	const email = useWatch<RegisterSchemaType, 'email'>({ name: 'email' });
	const { formState, trigger } = useFormContext<RegisterSchemaType>();
	const { dirtyFields, errors, touchedFields } = formState;
	const isTermsAndConditionsValid = isTermsAndConditionsVisible
		? !!dirtyFields.termsAndConditions && !errors.termsAndConditions
		: true;

	useEffect(() => {
		if (!touchedFields.email && email) trigger('email');
	}, [trigger, touchedFields.email, email]);

	useEffect(() => {
		onValidityChange(
			!!email && !!dirtyFields.isAbove18 && !errors.email && !errors.isAbove18 && isTermsAndConditionsValid
		);
	}, [
		dirtyFields.isAbove18,
		email,
		errors.email,
		errors.isAbove18,
		onValidityChange,
		dirtyFields.termsAndConditions,
		errors.termsAndConditions,
		isTermsAndConditionsValid
	]);
};

export const useUsernameStepValidation = ({
	isCountryStepVisible,
	isEmailStepVisible,
	onValidityChange
}: UsernameStepValidationProps) => {
	const username = useWatch<RegisterSchemaType, 'username'>({ name: 'username' });
	const { formState, trigger } = useFormContext<RegisterSchemaType>();
	const { dirtyFields, errors, touchedFields } = formState;

	const { suggestions } = useAtomValue(usernameRegistrationStepAtom);

	useEffect(() => {
		if (!touchedFields.username && username) trigger('username');
	}, [trigger, touchedFields.username, username]);

	useEffect(() => {
		const isCountryPortionValid =
			isCountryStepVisible || (!!dirtyFields.termsAndConditions && !errors.termsAndConditions);

		const isEmailPortionValid = isEmailStepVisible || (!!dirtyFields.isAbove18 && !errors.isAbove18);

		const isUsernamePortionValid = !!username && !errors.username && suggestions.length === 0;

		onValidityChange(isUsernamePortionValid && isEmailPortionValid && isCountryPortionValid);
	}, [
		isEmailStepVisible,
		isCountryStepVisible,
		dirtyFields.isAbove18,
		dirtyFields.termsAndConditions,
		errors.username,
		errors.termsAndConditions,
		errors.isAbove18,
		onValidityChange,
		suggestions.length,
		username
	]);
};

export const useCountryStepValidation = ({ onValidityChange }: StepValidationProps) => {
	const country = useWatch<RegisterSchemaType, 'country'>({ name: 'country' });
	const { formState, trigger } = useFormContext<RegisterSchemaType>();
	const { dirtyFields, errors, touchedFields } = formState;

	useEffect(() => {
		if (!touchedFields.country && country) trigger('country');
	}, [country, trigger, touchedFields.country]);

	useEffect(() => {
		onValidityChange(
			!!country && !!dirtyFields.termsAndConditions && !errors.country && !errors.termsAndConditions
		);
	}, [country, dirtyFields.termsAndConditions, errors.country, errors.termsAndConditions, onValidityChange]);
};
